import { canCheckout, checkoutHint, isValidFinishDart } from "../checkouts.js";
import { cricketTargets, dartsForTotal, isPossibleVisitTotal, isValidDart, pointsOf } from "../darts.js";
import { inGamePlayerName, snapshotCompletedLeg } from "../stats.js";
import type {
  ActionResult,
  ClientAction,
  CricketOptions,
  CricketScoring,
  DartThrow,
  GameType,
  InputSource,
  LegState,
  MatchConfig,
  MatchState,
  Player,
  PlayerLegState,
  Visit,
} from "../types.js";
import { DEFAULT_AUTODARTS_URL, STANDARD_CRICKET_NUMBERS } from "../types.js";
import { normalizeAutodartsUrl } from "../autodarts.js";
import {
  creditIndices,
  defaultTeamFirstThrower,
  isCheckoutFrozen,
  isTeamFormat,
  nextTeamFirstThrower,
  nextThrowerIndex,
  normalizeTeamPlay,
  opposingSideLeaders,
  opposingTeamOpenNumber,
  opponentIndices,
  partnerIndex,
  playingTeamCount,
  teamClosedNumber,
  teamCricketScore,
  teamSlotCount,
  teammateIndices,
  uniqueSideCount,
  winnerLabel,
} from "./teamPlay.js";
import {
  applyThrowSequence,
  HIGHSCORE_ROUNDS,
  sanitizeThrowSequence,
  soloFirstThrowerForCount,
  usesThrowOrderPopup,
} from "./throwSequence.js";

const MAX_UNDO = 80;

function clone<T>(value: T): T {
  return structuredClone(value);
}

function emptyMarks(targets: number[]): Record<string, number> {
  const marks: Record<string, number> = {};
  for (const n of targets) marks[String(n)] = 0;
  return marks;
}

function cricketNumbers(options: CricketOptions): number[] {
  return cricketTargets(options.numbers, options.includeBull);
}

function initPlayer(config: MatchConfig): PlayerLegState {
  const elimination = config.gameType === "elimination";
  return {
    remaining: elimination ? 0 : config.x01.startScore,
    opened: elimination ? config.elimination.inMode === "straight" : config.x01.inMode === "straight",
    marks: emptyMarks(cricketNumbers(config.cricket)),
    cricketScore: 0,
    lives: 0,
    eliminated: false,
    nextTarget: 1,
    shanghaiScore: 0,
    highscoreScore: 0,
    clockFinished: false,
  };
}

function createLeg(config: MatchConfig, firstThrowerIndex: number): LegState {
  const players = config.players.map(() => initPlayer(config));
  return {
    firstThrowerIndex,
    currentPlayerIndex: firstThrowerIndex,
    players,
    currentVisit: [],
    visitStartSnapshot: clone(players),
    visits: [],
    winnerIndex: null,
    bustMessage: null,
    eliminationTarget: config.gameType === "elimination" ? config.elimination.target : null,
    knockedPlayerIds: [],
    shanghaiRound: 1,
    roundVisits: 0,
  };
}

/** Stable default roster so a match can start without creating players. */
export const DEFAULT_PLAYERS: Player[] = [
  { id: "p1", name: "Spieler 1" },
  { id: "p2", name: "Spieler 2" },
];

export function defaultMatchLength(gameType: GameType): Pick<MatchConfig, "legsToWinSet" | "setsToWin"> {
  if (gameType === "x01") return { legsToWinSet: 3, setsToWin: 1 };
  return { legsToWinSet: 1, setsToWin: 1 };
}

export function createDefaultConfig(): MatchConfig {
  return {
    gameType: "x01",
    players: DEFAULT_PLAYERS.map((p) => ({ ...p })),
    firstThrowerIndex: 1,
    ...defaultMatchLength("x01"),
    inputMode: "total",
    inputSource: "pad",
    autodartsUrl: DEFAULT_AUTODARTS_URL,
    x01: { startScore: 501, inMode: "straight", outMode: "double" },
    cricket: { numbers: [...STANDARD_CRICKET_NUMBERS], includeBull: true, scoring: "normal" },
    elimination: { target: 301, inMode: "straight", outMode: "double", extreme: false },
    clock: { requireMode: "any" },
    shanghai: { endNumber: 7, shanghaiWins: true },
    teams: [],
    teamPlay: "solo",
    bullUpLastLeg: true,
    throwSequence: [0, 1],
  };
}

function emptySlot(): Player {
  return { id: "", name: "", teamId: null, teamName: null };
}

export function normalizeConfig(raw: MatchConfig): MatchConfig {
  const gameType = raw.gameType;
  const teamPlay = normalizeTeamPlay({ ...raw, gameType });
  const allowEmptySlots = teamPlay !== "solo";
  const players = (raw.players ?? [])
    .slice(0, 8)
    .map((p, i) => {
      const id = (p?.id ?? "").trim();
      const name = (p?.name ?? "").trim();
      const teamId = p?.teamId ?? null;
      const teamName = p?.teamName ?? null;
      if (allowEmptySlots && !id && !name) return emptySlot();
      const fallback = `Spieler ${i + 1}`;
      const rawName = name || fallback;
      return {
        id: id || (allowEmptySlots ? "" : `p${i + 1}`),
        name: inGamePlayerName(rawName) || fallback,
        teamId,
        teamName,
      };
    });
  while (players.length < 1) {
    players.push({ id: "p1", name: "Spieler 1", teamId: null, teamName: null });
  }
  const startScore = Math.min(10001, Math.max(2, Math.round(raw.x01?.startScore ?? 501)));
  const elimRaw = raw.elimination as (MatchConfig["elimination"] & { lives?: number; target?: number }) | undefined;
  const elimTarget = Math.min(
    10001,
    Math.max(2, Math.round(elimRaw?.target ?? 301)),
  );
  const endNumber = Math.min(20, Math.max(1, Math.round(raw.shanghai?.endNumber ?? 7)));
  const legsToWinSet =
    gameType === "highscore" ? 1 : Math.min(21, Math.max(1, Math.round(raw.legsToWinSet ?? 1)));
  const setsToWin =
    gameType === "highscore" ? 1 : Math.min(21, Math.max(1, Math.round(raw.setsToWin ?? 1)));
  let numbers = (raw.cricket?.numbers ?? [...STANDARD_CRICKET_NUMBERS])
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 20);
  numbers = [...new Set(numbers)];
  if (numbers.length === 0) numbers = [...STANDARD_CRICKET_NUMBERS];
  const includeBull = raw.cricket?.includeBull !== false;
  const scoring: CricketScoring =
    raw.cricket?.scoring === "cutThroat" || raw.cricket?.scoring === "noScore"
      ? raw.cricket.scoring
      : "normal";
  if (teamPlay !== "solo") {
    const need = teamSlotCount(playingTeamCount({ ...raw, gameType, teamPlay, players }));
    while (players.length < need) players.push(emptySlot());
    players.splice(need);
  }
  const seq = sanitizeThrowSequence(raw.throwSequence, players.length);
  const fallbackFirst =
    teamPlay !== "solo"
      ? defaultTeamFirstThrower(playingTeamCount({ ...raw, gameType, teamPlay, players }))
      : soloFirstThrowerForCount(gameType, players.length);
  const hasFirst = Number.isInteger(raw.firstThrowerIndex);
  const first =
    usesThrowOrderPopup(gameType) && teamPlay === "solo"
      ? (seq[0] ?? 0)
      : Math.min(
          Math.max(0, hasFirst ? raw.firstThrowerIndex : fallbackFirst),
          Math.max(0, players.length - 1),
        );
  let inputMode = raw.inputMode ?? "total";
  if (gameType === "cricket" || gameType === "clock" || gameType === "shanghai" || gameType === "elimination" || gameType === "highscore") {
    inputMode = "single";
  }
  const inputSource: InputSource = raw.inputSource === "autodarts" ? "autodarts" : "pad";
  const autodartsUrl = normalizeAutodartsUrl(raw.autodartsUrl) || DEFAULT_AUTODARTS_URL;
  return {
    gameType,
    players,
    firstThrowerIndex: first,
    legsToWinSet,
    setsToWin,
    inputMode,
    inputSource,
    autodartsUrl,
    x01: {
      startScore,
      inMode: raw.x01?.inMode === "double" ? "double" : "straight",
      outMode: raw.x01?.outMode === "straight" || raw.x01?.outMode === "master" ? raw.x01.outMode : "double",
    },
    cricket: { numbers, includeBull, scoring },
    elimination: {
      target: elimTarget,
      inMode: elimRaw?.inMode === "double" ? "double" : "straight",
      outMode: elimRaw?.outMode === "straight" || elimRaw?.outMode === "master" ? elimRaw.outMode : "double",
      extreme: elimRaw?.extreme === true,
    },
    clock: {
      requireMode:
        raw.clock?.requireMode === "double" || raw.clock?.requireMode === "triple"
          ? raw.clock.requireMode
          : "any",
    },
    shanghai: { endNumber, shanghaiWins: true },
    teams: ((raw.teams ?? (raw as { clubs?: MatchConfig["teams"] }).clubs) ?? [])
      .filter((c) => c?.id && c?.name)
      .slice(0, 16)
      .map((c) => ({ id: c.id, name: c.name.trim() })),
    teamPlay,
    bullUpLastLeg: gameType === "x01" && raw.bullUpLastLeg !== false,
    throwSequence: seq,
  };
}

export function createMatch(config: MatchConfig): MatchState {
  const normalized = normalizeConfig(config);
  const n = normalized.players.length;
  const starter = usesThrowOrderPopup(normalized.gameType)
    ? (normalized.throwSequence?.[0] ?? 0)
    : normalized.firstThrowerIndex;
  return {
    id: newMatchId(),
    config: normalized,
    status: "playing",
    currentLeg: createLeg(normalized, starter),
    matchVisits: [],
    legsWon: Array.from({ length: n }, () => 0),
    setsWon: Array.from({ length: n }, () => 0),
    currentSet: 1,
    currentLegInSet: 1,
    lastWinnerIndex: null,
    lastEvent: null,
    canUndo: false,
    matchLegsWon: Array.from({ length: n }, () => 0),
    legSeq: 1,
    legReports: [],
  };
}

function currentPlayerId(state: MatchState): string {
  return state.config.players[state.currentLeg.currentPlayerIndex]!.id;
}

function activeIndices(leg: LegState): number[] {
  return leg.players.map((p, i) => (p.eliminated ? -1 : i)).filter((i) => i >= 0);
}

function nextPlayerIndex(state: MatchState, from: number): number {
  const eliminated = state.currentLeg.players.map((p) => p.eliminated);
  return nextThrowerIndex(state.config, from, eliminated, state.currentLeg.firstThrowerIndex);
}

function beginVisit(leg: LegState): void {
  leg.currentVisit = [];
  leg.visitStartSnapshot = clone(leg.players);
  leg.bustMessage = null;
}

function restoreVisitStart(leg: LegState): void {
  leg.players = clone(leg.visitStartSnapshot);
}

function nextTurn(state: MatchState): void {
  const leg = state.currentLeg;
  leg.currentPlayerIndex = nextPlayerIndex(state, leg.currentPlayerIndex);
  beginVisit(leg);
}

function syncLeagueSide(state: MatchState, playerIndex: number): void {
  if (state.config.teamPlay !== "league" || !isTeamFormat(state.config)) return;
  const src = state.currentLeg.players[playerIndex];
  const dst = state.currentLeg.players[partnerIndex(playerIndex)];
  if (!src || !dst) return;
  dst.remaining = src.remaining;
  dst.opened = src.opened;
  dst.marks = { ...src.marks };
  dst.cricketScore = src.cricketScore;
}

function newMatchId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function recordVisit(state: MatchState, visit: Visit): void {
  if (!state.matchVisits) state.matchVisits = [];
  visit.legSeq = state.legSeq ?? 1;
  const idx = state.config.players.findIndex((p) => p.id === visit.playerId);
  const start = idx >= 0 ? state.currentLeg.visitStartSnapshot[idx] : undefined;
  const game = state.config.gameType;
  if ((game === "x01" || game === "elimination") && start) {
    const remaining =
      game === "x01" ? start.remaining : state.config.elimination.target - start.remaining;
    const outMode = game === "x01" ? state.config.x01.outMode : state.config.elimination.outMode;
    if (remaining > 0 && canCheckout(remaining, outMode, 3)) visit.checkoutAttempt = true;
    if (visit.checkout) visit.finishScore = remaining;
  }
  state.currentLeg.visits.push(visit);
  state.matchVisits.push(visit);
}

function finishVisitAndRotate(state: MatchState, visit: Visit): void {
  recordVisit(state, visit);
  nextTurn(state);
}

export function hydrateMatch(raw: MatchState): MatchState {
  const config = raw?.config ? normalizeConfig(raw.config) : createDefaultConfig();
  const fallbackLeg = createLeg(config, config.firstThrowerIndex);
  const visits = Array.isArray(raw?.matchVisits)
    ? raw.matchVisits
    : [...(raw?.currentLeg?.visits ?? [])];
  const n = config.players.length;
  const status = raw?.status;
  return {
    ...raw,
    id: raw?.id || newMatchId(),
    config,
    status:
      status === "playing" ||
      status === "legOver" ||
      status === "setOver" ||
      status === "matchOver" ||
      status === "bullUp"
        ? status
        : "playing",
    matchVisits: visits,
    currentLeg: {
      ...fallbackLeg,
      ...(raw?.currentLeg ?? {}),
      knockedPlayerIds: raw?.currentLeg?.knockedPlayerIds ?? [],
      players:
        Array.isArray(raw?.currentLeg?.players) && raw.currentLeg.players.length === n
          ? raw.currentLeg.players.map((p, i) => ({
              ...fallbackLeg.players[i]!,
              ...p,
              highscoreScore: p.highscoreScore ?? 0,
            }))
          : fallbackLeg.players,
      firstThrowerIndex: raw?.currentLeg?.firstThrowerIndex ?? fallbackLeg.firstThrowerIndex,
      currentPlayerIndex: raw?.currentLeg?.currentPlayerIndex ?? fallbackLeg.currentPlayerIndex,
    },
    matchLegsWon: raw?.matchLegsWon ?? Array.from({ length: n }, () => 0),
    legsWon: Array.isArray(raw?.legsWon) ? raw.legsWon : Array.from({ length: n }, () => 0),
    setsWon: Array.isArray(raw?.setsWon) ? raw.setsWon : Array.from({ length: n }, () => 0),
    legSeq: raw?.legSeq ?? 1,
    legReports: Array.isArray(raw?.legReports) ? raw.legReports : [],
  };
}

const undoStacks = new WeakMap<MatchState, MatchState[]>();

function pushUndo(from: MatchState, next: MatchState): MatchState {
  const stack = undoStacks.get(from) ?? [];
  const copy = clone(from);
  undoStacks.delete(from);
  const newStack = [...stack, copy].slice(-MAX_UNDO);
  undoStacks.set(next, newStack);
  next.canUndo = newStack.length > 0;
  return next;
}

function popUndo(state: MatchState): MatchState | null {
  const stack = undoStacks.get(state);
  if (!stack || stack.length === 0) return null;
  const prev = stack.pop()!;
  undoStacks.set(prev, stack);
  prev.canUndo = stack.length > 0;
  return prev;
}

function assertPlaying(state: MatchState): string | null {
  if (state.status !== "playing") return "Spiel ist nicht aktiv.";
  return null;
}

function assertTurn(state: MatchState, playerId: string): string | null {
  const playing = assertPlaying(state);
  if (playing) return playing;
  if (playerId !== currentPlayerId(state)) return "Nicht an der Reihe.";
  const p = state.currentLeg.players[state.currentLeg.currentPlayerIndex];
  if (p?.eliminated) return "Spieler ist ausgeschieden.";
  return null;
}

function applyX01Dart(state: MatchState, dart: DartThrow): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;
  const { outMode } = next.config.x01;

  if (leg.currentVisit.length === 0) {
    leg.visitStartSnapshot = clone(leg.players);
  }

  let scored = 0;
  if (!player.opened) {
    if (dart.multiplier === 2 && dart.segment > 0) {
      player.opened = true;
      scored = pointsOf(dart);
    } else {
      scored = 0;
    }
  } else {
    scored = pointsOf(dart);
  }

  const remainingAfter = player.remaining - scored;
  player.remaining = remainingAfter;
  syncLeagueSide(next, idx);
  leg.currentVisit.push(dart);
  next.lastEvent = null;

  const bust = (reason: string): ActionResult => {
    restoreVisitStart(leg);
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total: 0,
      kind: "darts",
      bust: true,
    };
    leg.bustMessage = reason;
    next.lastEvent = reason;
    finishVisitAndRotate(next, visit);
    return { ok: true, state: next };
  };

  if (!player.opened) {
    if (leg.currentVisit.length >= 3) {
      const visit: Visit = {
        playerId: next.config.players[idx]!.id,
        darts: [...leg.currentVisit],
        total: 0,
        kind: "darts",
      };
      finishVisitAndRotate(next, visit);
    }
    return { ok: true, state: next };
  }

  if (remainingAfter < 0) return bust("Bust – überworfen");
  if (remainingAfter === 0) {
    if (!isValidFinishDart(dart, outMode)) {
      return bust(outMode === "double" ? "Bust – kein Doppel-Out" : "Bust – kein gültiger Finish");
    }
    if (next.config.teamPlay === "team" && isCheckoutFrozen(leg, idx)) {
      return bust("Ausmachen gesperrt – Partner hat mehr Rest als jedes gegnerische Team");
    }
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total: leg.visitStartSnapshot[idx]!.remaining,
      kind: "darts",
      checkout: true,
    };
    recordVisit(next, visit);
    return completeLeg(next, idx);
  }
  if (remainingAfter === 1 && outMode !== "straight") {
    return bust("Bust – Rest 1");
  }

  if (leg.currentVisit.length >= 3) {
    const total = leg.visitStartSnapshot[idx]!.remaining - player.remaining;
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total,
      kind: "darts",
    };
    finishVisitAndRotate(next, visit);
  }
  return { ok: true, state: next };
}

function applyX01Total(state: MatchState, total: number): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;
  const { inMode, outMode } = next.config.x01;
  const darts = dartsForTotal(total) ?? [
    { segment: 0, multiplier: 1 as const },
    { segment: 0, multiplier: 1 as const },
    { segment: 0, multiplier: 1 as const },
  ];

  if (!player.opened) {
    if (total === 0) {
      const visit: Visit = { playerId: next.config.players[idx]!.id, darts, total: 0, kind: "total" };
      finishVisitAndRotate(next, visit);
      return { ok: true, state: next };
    }
    if (inMode === "double") player.opened = true;
  }

  const remainingAfter = player.remaining - total;
  const bust = (reason: string): ActionResult => {
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts,
      total: 0,
      kind: "total",
      bust: true,
    };
    leg.bustMessage = reason;
    next.lastEvent = reason;
    finishVisitAndRotate(next, visit);
    return { ok: true, state: next };
  };

  if (remainingAfter < 0) return bust("Bust – überworfen");
  if (remainingAfter === 0) {
    if (!canCheckout(player.remaining, outMode, 3)) {
      return bust(outMode === "double" ? "Bust – kein Doppel-Out" : "Bust – kein gültiger Finish");
    }
    if (next.config.teamPlay === "team" && isCheckoutFrozen(leg, idx)) {
      return bust("Ausmachen gesperrt – Partner hat mehr Rest als jedes gegnerische Team");
    }
    player.remaining = 0;
    syncLeagueSide(next, idx);
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts,
      total,
      kind: "total",
      checkout: true,
    };
    recordVisit(next, visit);
    return completeLeg(next, idx);
  }
  if (remainingAfter === 1 && outMode !== "straight") return bust("Bust – Rest 1");

  player.remaining = remainingAfter;
  syncLeagueSide(next, idx);
  const visit: Visit = { playerId: next.config.players[idx]!.id, darts, total, kind: "total" };
  finishVisitAndRotate(next, visit);
  return { ok: true, state: next };
}

function applyCricketDart(state: MatchState, dart: DartThrow): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;
  const targets = cricketNumbers(next.config.cricket);
  const scoring = next.config.cricket.scoring ?? "normal";

  if (leg.currentVisit.length === 0) {
    leg.visitStartSnapshot = clone(leg.players);
  }

  const segment = dart.segment;
  const isTarget = targets.includes(segment);
  if (isTarget && dart.segment > 0) {
    let marksToAdd = dart.multiplier;
    if (segment === 25) marksToAdd = dart.multiplier === 2 ? 2 : 1;
    const key = String(segment);
    while (marksToAdd > 0) {
      const current = player.marks[key] ?? 0;
      if (current < 3) {
        player.marks[key] = current + 1;
        syncLeagueSide(next, idx);
      } else if (scoring !== "noScore") {
        applyCricketExtra(next, idx, key, segment, scoring);
      }
      marksToAdd -= 1;
    }
  }

  leg.currentVisit.push(dart);

  if (cricketSideHasWon(next, idx, targets, scoring)) {
    const total = leg.currentVisit.reduce((s, d) => s + pointsOf(d), 0);
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total,
      kind: "darts",
      checkout: true,
    };
    recordVisit(next, visit);
    return completeLeg(next, idx);
  }

  if (leg.currentVisit.length >= 3) {
    const total = leg.currentVisit.reduce((s, d) => s + pointsOf(d), 0);
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total,
      kind: "darts",
    };
    finishVisitAndRotate(next, visit);
  }
  return { ok: true, state: next };
}

function applyCricketExtra(
  state: MatchState,
  idx: number,
  key: string,
  segment: number,
  scoring: CricketScoring,
): void {
  const leg = state.currentLeg;
  const player = leg.players[idx]!;
  const teamish = isTeamFormat(state.config);
  if (state.config.teamPlay === "team" && !teamClosedNumber(leg, idx, key)) return;

  if (scoring === "cutThroat") {
    if (state.config.teamPlay === "league") {
      for (const opp of opposingSideLeaders(idx, leg.players.length)) {
        if ((leg.players[opp]?.marks[key] ?? 0) < 3) {
          leg.players[opp]!.cricketScore += segment;
          syncLeagueSide(state, opp);
        }
      }
      return;
    }
    const others = teamish ? opponentIndices(idx, leg.players.length) : leg.players.map((_, i) => i).filter((i) => i !== idx);
    for (const i of others) {
      if ((leg.players[i]!.marks[key] ?? 0) < 3) {
        leg.players[i]!.cricketScore += segment;
      }
    }
    return;
  }

  const anyoneOpen = teamish
    ? opposingTeamOpenNumber(leg, idx, key)
    : leg.players.some((p, i) => i !== idx && (p.marks[key] ?? 0) < 3);
  if (anyoneOpen) {
    player.cricketScore += segment;
    syncLeagueSide(state, idx);
  }
}

function cricketHasWon(
  player: PlayerLegState,
  all: PlayerLegState[],
  targets: number[],
  scoring: CricketScoring,
): boolean {
  const closed = targets.every((n) => (player.marks[String(n)] ?? 0) >= 3);
  if (!closed) return false;
  if (scoring === "noScore") return true;
  if (scoring === "cutThroat") {
    return all.every((p) => player.cricketScore <= p.cricketScore);
  }
  return all.every((p) => player.cricketScore >= p.cricketScore);
}

function cricketSideHasWon(
  state: MatchState,
  idx: number,
  targets: number[],
  scoring: CricketScoring,
): boolean {
  const player = state.currentLeg.players[idx]!;
  if (state.config.teamPlay === "team") {
    const bothClosed = teammateIndices(idx).every((i) =>
      targets.every((n) => (state.currentLeg.players[i]?.marks[String(n)] ?? 0) >= 3),
    );
    if (!bothClosed) return false;
    if (scoring === "noScore") return true;
    const mine = teamCricketScore(state.currentLeg, idx);
    const others = opposingSideLeaders(idx, state.currentLeg.players.length).map((i) =>
      teamCricketScore(state.currentLeg, i),
    );
    if (scoring === "cutThroat") return others.every((score) => mine <= score);
    return others.every((score) => mine >= score);
  }
  return cricketHasWon(player, state.currentLeg.players, targets, scoring);
}

function clockHit(dart: DartThrow, target: number, requireMode: "any" | "double" | "triple"): boolean {
  if (target === 25) {
    if (requireMode === "double") return dart.segment === 25 && dart.multiplier === 2;
    return dart.segment === 25;
  }
  if (dart.segment !== target) return false;
  if (requireMode === "double") return dart.multiplier === 2;
  if (requireMode === "triple") return dart.multiplier === 3;
  return true;
}

/** 1…20, then Bull (25). `null` means the board is finished. */
function nextClockNumber(current: number): number | null {
  if (current >= 25) return null;
  if (current === 20) return 25;
  if (current >= 1 && current < 20) return current + 1;
  return 25;
}

/** Advance `steps` numbers past the current target (1 = next, 2 = skip one, 3 = skip two). */
function advanceClock(current: number, steps: number): { target: number; finished: boolean } {
  let target = current;
  for (let i = 0; i < steps; i++) {
    const next = nextClockNumber(target);
    if (next == null) return { target: 26, finished: true };
    target = next;
  }
  return { target, finished: false };
}

function applyClockDart(state: MatchState, dart: DartThrow): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;
  const mode = next.config.clock.requireMode;

  if (leg.currentVisit.length === 0) {
    leg.visitStartSnapshot = clone(leg.players);
  }

  if (!player.clockFinished && clockHit(dart, player.nextTarget, mode)) {
    const steps = mode === "any" ? dart.multiplier : 1;
    const { target, finished } = advanceClock(player.nextTarget, steps);
    if (finished) {
      player.clockFinished = true;
      player.nextTarget = 26;
      leg.currentVisit.push(dart);
      const visit: Visit = {
        playerId: next.config.players[idx]!.id,
        darts: [...leg.currentVisit],
        total: 0,
        kind: "darts",
        checkout: true,
      };
      recordVisit(next, visit);
      return completeLeg(next, idx);
    }
    player.nextTarget = target;
  }

  leg.currentVisit.push(dart);
  if (leg.currentVisit.length >= 3) {
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total: 0,
      kind: "darts",
    };
    finishVisitAndRotate(next, visit);
  }
  return { ok: true, state: next };
}

function shanghaiTarget(n: number): number {
  if (n >= 21) return 25;
  if (n < 1) return 1;
  return n;
}

function nextShanghaiTarget(current: number): number {
  const n = shanghaiTarget(current);
  if (n >= 20) return 25;
  return n + 1;
}

function sequentialShanghai(
  startTarget: number,
  darts: DartThrow[],
): { single: boolean; double: boolean; triple: boolean; score: number; shanghai: boolean } {
  let target = shanghaiTarget(startTarget);
  let single = false;
  let dbl = false;
  let triple = false;
  let score = 0;
  const hitNumbers = new Set<number>();
  for (const d of darts) {
    if (d.segment !== target || d.segment <= 0) continue;
    score += target * d.multiplier;
    hitNumbers.add(target);
    if (d.multiplier === 1) single = true;
    if (d.multiplier === 2) dbl = true;
    if (d.multiplier === 3) triple = true;
    target = nextShanghaiTarget(target);
  }
  return {
    single,
    double: dbl,
    triple,
    score,
    shanghai: single && dbl && triple && hitNumbers.size >= 3,
  };
}

function applyShanghaiDart(state: MatchState, dart: DartThrow): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;

  player.nextTarget = shanghaiTarget(player.nextTarget);

  if (leg.currentVisit.length === 0) {
    leg.visitStartSnapshot = clone(leg.players);
  }
  if (dart.segment === player.nextTarget && dart.segment > 0) {
    player.shanghaiScore += player.nextTarget * dart.multiplier;
    player.nextTarget = nextShanghaiTarget(player.nextTarget);
  }
  leg.currentVisit.push(dart);
  next.lastEvent = null;

  const startTarget = leg.visitStartSnapshot[idx]!.nextTarget;
  const hits = sequentialShanghai(startTarget, leg.currentVisit);

  if (hits.shanghai && next.config.shanghai.shanghaiWins) {
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total: hits.score,
      kind: "darts",
      shanghai: true,
      checkout: true,
    };
    recordVisit(next, visit);
    next.lastEvent = "Shanghai!";
    return completeLeg(next, idx);
  }

  if (leg.currentVisit.length >= 3) {
    return completeShanghaiVisit(next, idx, hits.score, hits.shanghai);
  }
  return { ok: true, state: next };
}

function completeShanghaiVisit(
  next: MatchState,
  idx: number,
  visitScore: number,
  isShanghai: boolean,
): ActionResult {
  const leg = next.currentLeg;
  const round = leg.shanghaiRound;
  const visit: Visit = {
    playerId: next.config.players[idx]!.id,
    darts: [...leg.currentVisit],
    total: visitScore,
    kind: "darts",
    shanghai: isShanghai,
  };

  recordVisit(next, visit);
  leg.roundVisits += 1;
  const active = activeIndices(leg).length;
  if (leg.roundVisits >= active) {
    if (round >= next.config.shanghai.endNumber) {
      return finishShanghaiGame(next);
    }
    leg.shanghaiRound = round + 1;
    leg.roundVisits = 0;
    nextTurn(next);
    return { ok: true, state: next };
  }
  nextTurn(next);
  return { ok: true, state: next };
}

function finishShanghaiGame(state: MatchState): ActionResult {
  const scores = state.currentLeg.players.map((p) => p.shanghaiScore);
  const max = Math.max(...scores);
  const winners = scores.map((s, i) => (s === max ? i : -1)).filter((i) => i >= 0);
  if (winners.length === 1) {
    return completeLeg(state, winners[0]!);
  }
  state.currentLeg.shanghaiRound += 1;
  state.currentLeg.roundVisits = 0;
  state.lastEvent = `Gleichstand – weiter bis jemand führt (Runde ${state.currentLeg.shanghaiRound})`;
  nextTurn(state);
  return { ok: true, state };
}

function dartPoints(dart: DartThrow): number {
  if (!dart.segment) return 0;
  return dart.segment * dart.multiplier;
}

function applyHighscoreDart(state: MatchState, dart: DartThrow): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;
  if (leg.currentVisit.length === 0) {
    leg.visitStartSnapshot = clone(leg.players);
  }
  player.highscoreScore += dartPoints(dart);
  leg.currentVisit.push(dart);
  next.lastEvent = null;
  if (leg.currentVisit.length >= 3) {
    const visitScore = leg.currentVisit.reduce((sum, d) => sum + dartPoints(d), 0);
    const visit: Visit = {
      playerId: next.config.players[idx]!.id,
      darts: [...leg.currentVisit],
      total: visitScore,
      kind: "darts",
    };
    recordVisit(next, visit);
    leg.roundVisits += 1;
    const active = activeIndices(leg).length;
    if (leg.roundVisits >= active) {
      if (leg.shanghaiRound >= HIGHSCORE_ROUNDS) {
        return finishHighscoreGame(next);
      }
      leg.shanghaiRound += 1;
      leg.roundVisits = 0;
    }
    nextTurn(next);
  }
  return { ok: true, state: next };
}

function finishHighscoreGame(state: MatchState): ActionResult {
  const scores = state.currentLeg.players.map((p) => p.highscoreScore ?? 0);
  const max = Math.max(...scores);
  const winners = scores.map((s, i) => (s === max ? i : -1)).filter((i) => i >= 0);
  if (winners.length === 1) {
    return completeLeg(state, winners[0]!);
  }
  state.currentLeg.shanghaiRound += 1;
  state.currentLeg.roundVisits = 0;
  state.lastEvent = `Gleichstand – weiter bis jemand führt (Runde ${state.currentLeg.shanghaiRound})`;
  nextTurn(state);
  return { ok: true, state };
}

function playerNamesForIds(state: MatchState, ids: string[]): string[] {
  return ids.map((id) => state.config.players.find((p) => p.id === id)?.name).filter((n): n is string => Boolean(n));
}

function applyKnockOff(state: MatchState, throwerIdx: number): string[] {
  const leg = state.currentLeg;
  const score = leg.players[throwerIdx]!.remaining;
  const ids: string[] = [];
  if (score <= 0) return ids;
  const openedAfterReset = state.config.elimination.inMode === "straight";
  for (let i = 0; i < leg.players.length; i++) {
    if (i === throwerIdx) continue;
    const other = leg.players[i]!;
    if (other.remaining !== score) continue;
    other.remaining = 0;
    other.opened = openedAfterReset;
    ids.push(state.config.players[i]!.id);
  }
  return ids;
}

function finishEliminationVisit(
  next: MatchState,
  idx: number,
  opts: { bust?: boolean; checkout?: boolean },
): ActionResult {
  const leg = next.currentLeg;
  const darts = [...leg.currentVisit];
  const total = opts.bust ? 0 : darts.reduce((sum, dart) => sum + pointsOf(dart), 0);
  const visit: Visit = {
    playerId: next.config.players[idx]!.id,
    darts,
    total,
    kind: "darts",
    bust: opts.bust,
    checkout: opts.checkout,
    knocked: (leg.knockedPlayerIds?.length ?? 0) > 0,
  };
  if (opts.checkout) {
    recordVisit(next, visit);
    return completeLeg(next, idx);
  }
  finishVisitAndRotate(next, visit);
  return { ok: true, state: next };
}

function applyEliminationDart(state: MatchState, dart: DartThrow): ActionResult {
  const next = clone(state);
  const leg = next.currentLeg;
  const idx = leg.currentPlayerIndex;
  const player = leg.players[idx]!;
  const { outMode, target, extreme } = next.config.elimination;

  if (leg.currentVisit.length === 0) {
    leg.visitStartSnapshot = clone(leg.players);
  }
  leg.knockedPlayerIds = [];
  next.lastEvent = null;

  if (!player.opened) {
    if (dart.multiplier === 2 && dart.segment > 0) {
      player.opened = true;
      player.remaining += pointsOf(dart);
    }
  } else {
    player.remaining += pointsOf(dart);
  }
  const projected = player.remaining;
  leg.currentVisit.push(dart);

  const bust = (reason: string): ActionResult => {
    restoreVisitStart(leg);
    leg.knockedPlayerIds = [];
    leg.bustMessage = reason;
    next.lastEvent = reason;
    return finishEliminationVisit(next, idx, { bust: true });
  };

  if (!player.opened) {
    if (leg.currentVisit.length >= 3) return finishEliminationVisit(next, idx, {});
    return { ok: true, state: next };
  }

  if (projected > target) {
    if (extreme) {
      const adjusted = Math.max(0, 2 * target - projected);
      player.remaining = adjusted;
      const knocked = applyKnockOff(next, idx);
      leg.knockedPlayerIds = knocked;
      const names = playerNamesForIds(next, knocked);
      const over = `Überhang – Score ${adjusted}`;
      next.lastEvent = names.length ? `${over}. ${names.join(", ")} auf 0` : over;
      leg.bustMessage = over;
      return finishEliminationVisit(next, idx, {});
    }
    return bust("Bust – überworfen");
  }

  if (projected === target) {
    if (!isValidFinishDart(dart, outMode)) {
      return bust(outMode === "double" ? "Bust – kein Doppel-Out" : "Bust – kein gültiger Finish");
    }
    return finishEliminationVisit(next, idx, { checkout: true });
  }

  if (target - projected === 1 && outMode !== "straight") {
    return bust("Bust – Rest 1");
  }

  const knocked = applyKnockOff(next, idx);
  leg.knockedPlayerIds = knocked;
  if (knocked.length) {
    const names = playerNamesForIds(next, knocked);
    next.lastEvent = `${next.config.players[idx]!.name} setzt ${names.join(" und ")} auf 0`;
  }

  if (leg.currentVisit.length >= 3) return finishEliminationVisit(next, idx, {});
  return { ok: true, state: next };
}

function completeLeg(state: MatchState, winnerIndex: number): ActionResult {
  const next = state;
  next.currentLeg.winnerIndex = winnerIndex;
  next.lastWinnerIndex = winnerIndex;
  const credited = creditIndices(next.config, winnerIndex);
  for (const i of credited) {
    next.legsWon[i] = (next.legsWon[i] ?? 0) + 1;
  }
  if (!next.matchLegsWon || next.matchLegsWon.length !== next.config.players.length) {
    next.matchLegsWon = Array.from({ length: next.config.players.length }, () => 0);
  }
  for (const i of credited) {
    next.matchLegsWon[i] = (next.matchLegsWon[i] ?? 0) + 1;
  }
  if (!next.legReports) next.legReports = [];
  next.legReports = [...next.legReports, snapshotCompletedLeg(next, winnerIndex)];
  const name = winnerLabel(next.config, winnerIndex);
  next.lastEvent = `${name} gewinnt das Leg`;

  if ((next.legsWon[winnerIndex] ?? 0) >= next.config.legsToWinSet) {
    for (const i of credited) {
      next.setsWon[i] = (next.setsWon[i] ?? 0) + 1;
    }
    if ((next.setsWon[winnerIndex] ?? 0) >= next.config.setsToWin) {
      next.status = "matchOver";
      next.lastEvent = `${name} gewinnt das Match`;
      return { ok: true, state: next };
    }
    next.status = "setOver";
    next.lastEvent = `${name} gewinnt den Satz`;
    return { ok: true, state: next };
  }
  next.status = "legOver";
  return { ok: true, state: next };
}

function shouldBullUpNext(state: MatchState): boolean {
  if (state.config.gameType !== "x01") return false;
  if (state.config.bullUpLastLeg === false) return false;
  const need = Math.max(1, state.config.legsToWinSet);
  if (need < 2) return false;
  const oneAway = uniqueSideCount(state.legsWon ?? [], state.config, need - 1);
  return oneAway >= 2;
}

/** True while the deciding last leg is waiting for / was started via Ausbullen. */
export function isBullUpDecidingLeg(state: MatchState): boolean {
  return shouldBullUpNext(state);
}

function resetOpenVisit(leg: LegState): void {
  restoreVisitStart(leg);
  beginVisit(leg);
  leg.knockedPlayerIds = [];
}

function startNextLeg(state: MatchState): MatchState {
  const n = state.config.players.length;
  const prevFirst = state.currentLeg.firstThrowerIndex ?? 0;
  let nextFirst: number;
  if (isTeamFormat(state.config)) {
    nextFirst = nextTeamFirstThrower(state.config, prevFirst);
  } else if (usesThrowOrderPopup(state.config.gameType)) {
    const seq = sanitizeThrowSequence(state.config.throwSequence, n);
    const pos = seq.indexOf(prevFirst);
    nextFirst = seq[(pos >= 0 ? pos + 1 : 1) % seq.length]!;
  } else {
    nextFirst = (prevFirst + 1) % n;
  }
  const resetLegs = state.status === "setOver";
  const next = clone(state);
  if (resetLegs) {
    next.legsWon = Array.from({ length: n }, () => 0);
    next.currentSet += 1;
    next.currentLegInSet = 1;
  } else {
    next.currentLegInSet += 1;
  }
  next.currentLeg = createLeg(next.config, nextFirst);
  next.legSeq = (next.legSeq ?? 1) + 1;
  next.status = "playing";
  next.lastEvent = null;
  return next;
}

function applyThrow(state: MatchState, dart: DartThrow): ActionResult {
  if (!isValidDart(dart)) return { ok: false, error: "Ungültiger Wurf.", state };
  if (state.currentLeg.currentVisit.length >= 3) {
    return { ok: false, error: "Aufnahme ist voll.", state };
  }
  switch (state.config.gameType) {
    case "x01":
      return applyX01Dart(state, dart);
    case "cricket":
      return applyCricketDart(state, dart);
    case "elimination":
      return applyEliminationDart(state, dart);
    case "clock":
      return applyClockDart(state, dart);
    case "shanghai":
      return applyShanghaiDart(state, dart);
    case "highscore":
      return applyHighscoreDart(state, dart);
  }
}

function applyTotal(state: MatchState, total: number): ActionResult {
  if (!isPossibleVisitTotal(total)) {
    return { ok: false, error: "Unmögliche 3-Dart-Summe.", state };
  }
  const game = state.config.gameType;
  if (
    game === "cricket" ||
    game === "clock" ||
    game === "shanghai" ||
    game === "elimination" ||
    game === "highscore"
  ) {
    return { ok: false, error: "Gesamteingabe ist in diesem Spiel nicht möglich.", state };
  }
  if (state.currentLeg.currentVisit.length > 0) {
    return { ok: false, error: "Aufnahme läuft bereits – zuerst bestätigen oder rückgängig.", state };
  }
  if (game === "x01") return applyX01Total(state, total);
  return { ok: false, error: "Gesamteingabe ist in diesem Spiel nicht möglich.", state };
}

function applyConfirm(state: MatchState): ActionResult {
  const visit = state.currentLeg.currentVisit;
  if (visit.length === 0) return { ok: false, error: "Keine Darts eingegeben.", state };
  const miss: DartThrow = { segment: 0, multiplier: 1 };
  let current: ActionResult = { ok: true, state };
  while (
    current.ok &&
    current.state.status === "playing" &&
    current.state.currentLeg.currentVisit.length > 0 &&
    current.state.currentLeg.currentVisit.length < 3
  ) {
    current = applyThrow(current.state, miss);
  }
  return current;
}

export function applyAction(state: MatchState, action: ClientAction): ActionResult {
  if (action.type === "UNDO") {
    const prev = popUndo(state);
    if (!prev) return { ok: false, error: "Nichts rückgängig zu machen.", state };
    return { ok: true, state: prev };
  }

  if (action.type === "SET_INPUT_MODE") {
    const next = clone(state);
    const game = next.config.gameType;
    if (game === "cricket" || game === "clock" || game === "shanghai" || game === "elimination" || game === "highscore") {
      next.config.inputMode = "single";
    } else {
      next.config.inputMode = action.mode;
    }
    const stack = undoStacks.get(state) ?? [];
    undoStacks.set(next, stack);
    next.canUndo = stack.length > 0;
    return { ok: true, state: next };
  }

  if (action.type === "SET_INPUT_SOURCE") {
    const next = clone(state);
    next.config.inputSource = action.source === "autodarts" ? "autodarts" : "pad";
    if (next.config.inputSource === "autodarts") next.config.inputMode = "single";
    const stack = undoStacks.get(state) ?? [];
    undoStacks.set(next, stack);
    next.canUndo = stack.length > 0;
    return { ok: true, state: next };
  }

  if (action.type === "ACKNOWLEDGE") {
    if (state.status === "matchOver") {
      return { ok: false, error: "Match ist beendet.", state };
    }
    if (state.status !== "legOver" && state.status !== "setOver") {
      return { ok: false, error: "Kein Abschluss zum Bestätigen.", state };
    }
    const bullUp = state.status === "legOver" && shouldBullUpNext(state);
    const next = startNextLeg(state);
    if (bullUp) next.status = "bullUp";
    return { ok: true, state: next };
  }

  if (action.type === "REOPEN_BULL_UP") {
    if (state.status !== "playing") {
      return { ok: false, error: "Ausbullen kann jetzt nicht geändert werden.", state };
    }
    if (!shouldBullUpNext(state)) {
      return { ok: false, error: "Kein Ausbullen in diesem Leg.", state };
    }
    if ((state.currentLeg.visits ?? []).length > 0) {
      return { ok: false, error: "Erst Aufnahme zurücknehmen.", state };
    }
    const next = clone(state);
    resetOpenVisit(next.currentLeg);
    next.status = "bullUp";
    next.lastEvent = null;
    undoStacks.set(next, []);
    next.canUndo = false;
    return { ok: true, state: next };
  }

  if (action.type === "SET_LEG_STARTER") {
    if (state.status !== "bullUp") {
      return { ok: false, error: "Kein Ausbullen aktiv.", state };
    }
    const n = Math.max(1, state.config.players.length);
    const idx = Math.min(Math.max(0, Math.round(action.playerIndex)), n - 1);
    const next = clone(state);
    resetOpenVisit(next.currentLeg);
    next.currentLeg.firstThrowerIndex = idx;
    next.currentLeg.currentPlayerIndex = idx;
    next.status = "playing";
    next.lastEvent = null;
    undoStacks.set(next, []);
    next.canUndo = false;
    return { ok: true, state: next };
  }

  if (action.type === "REMATCH") {
    const config = action.throwSequence
      ? applyThrowSequence(state.config, action.throwSequence)
      : state.config;
    const next = createMatch(config);
    return { ok: true, state: next };
  }

  const err =
    action.type === "THROW_DART" || action.type === "SET_VISIT_TOTAL" || action.type === "CONFIRM_VISIT"
      ? assertTurn(state, action.playerId)
      : null;
  if (err) return { ok: false, error: err, state };

  let result: ActionResult;
  if (action.type === "THROW_DART") result = applyThrow(state, action.dart);
  else if (action.type === "SET_VISIT_TOTAL") result = applyTotal(state, action.total);
  else if (action.type === "CONFIRM_VISIT") result = applyConfirm(state);
  else return { ok: false, error: "Unbekannte Aktion.", state };

  if (!result.ok) return result;
  return { ok: true, state: pushUndo(state, result.state) };
}

export function currentVisitTotal(leg: LegState): number {
  return leg.currentVisit.reduce((s, d) => s + pointsOf(d), 0);
}

export function remainingPreview(state: MatchState): number | null {
  const idx = state.currentLeg.currentPlayerIndex;
  if (state.config.gameType === "x01") {
    return state.currentLeg.players[idx]!.remaining;
  }
  if (state.config.gameType === "elimination") {
    const player = state.currentLeg.players[idx]!;
    if (!player.opened) return null;
    return state.config.elimination.target - player.remaining;
  }
  return null;
}

export function hintForState(state: MatchState): string | null {
  const outMode =
    state.config.gameType === "x01"
      ? state.config.x01.outMode
      : state.config.gameType === "elimination"
        ? state.config.elimination.outMode
        : null;
  if (!outMode || outMode === "straight") return null;
  const remaining = remainingPreview(state);
  if (remaining == null) return null;
  const idx = state.currentLeg.currentPlayerIndex;
  if (state.config.gameType === "x01" && state.config.teamPlay === "team" && isCheckoutFrozen(state.currentLeg, idx)) {
    return "Ausmachen gesperrt";
  }
  const dartsLeft = 3 - state.currentLeg.currentVisit.length;
  return checkoutHint(remaining, outMode, Math.max(1, dartsLeft));
}

export function lastVisitForPlayer(state: MatchState, playerId: string): Visit | undefined {
  const visits = state.currentLeg.visits;
  for (let i = visits.length - 1; i >= 0; i--) {
    if (visits[i]!.playerId === playerId) return visits[i];
  }
  return undefined;
}

/** Current visit-round of the leg (1-based). Shanghai uses the number being thrown. */
export function currentLegRound(state: MatchState): number {
  if (state.config.gameType === "shanghai" || state.config.gameType === "highscore") {
    return Math.max(1, state.currentLeg.shanghaiRound || 1);
  }
  const n = Math.max(1, state.config.players.length);
  return Math.floor((state.currentLeg.visits?.length ?? 0) / n) + 1;
}

export { cricketNumbers };
